/**
 * JobStatus — long-running job display with retry semantics.
 * Shows state, attempts, and next retry; the UI polls but never fakes
 * completion: a job is "done" only when the server reports a terminal state.
 */

import type { ReactElement } from "react";

export interface JobView {
  readonly id: string;
  readonly kind: string;
  readonly state: "queued" | "claimed" | "succeeded" | "failed" | "canceled" | "dead";
  readonly attempts: number;
  readonly maxAttempts?: number;
  readonly nextRetryAt?: number;
}

const TERMINAL: readonly JobView["state"][] = ["succeeded", "canceled", "dead"];

export function isTerminal(state: JobView["state"]): boolean {
  return TERMINAL.includes(state);
}

export function JobStatus({ job }: { job: JobView }): ReactElement {
  const done = isTerminal(job.state);
  return (
    <div className={`job job-${job.state}`} data-job-id={job.id}>
      <span className="job-kind">{job.kind}</span>
      <span className="job-state">{job.state}</span>
      <span className="job-attempts">
        {job.attempts}
        {job.maxAttempts === undefined ? "" : `/${job.maxAttempts}`} attempts
      </span>
      {!done && job.nextRetryAt !== undefined ? (
        <span className="job-retry">retry at {new Date(job.nextRetryAt).toISOString()}</span>
      ) : null}
    </div>
  );
}
